/* eslint-disable import/order */
import '@mantine/core/styles.css';
import '@mantine/tiptap/styles.css';
import '@formatjs/intl-relativetimeformat/locale-data/nl';
import '@formatjs/intl-relativetimeformat/polyfill';
import { init as initSentry } from '@sentry/browser';
import 'moment/locale/nl';
import 'ol/ol.css';
import 'pannellum-react/lib/pannellum/css/pannellum.css';
import { parse as parseQueryString } from 'query-string';
import React from 'react';
import { createRoot } from 'react-dom/client';
import 'react-image-gallery/styles/css/image-gallery.css';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import 'react-widgets/styles.css';
import { ValidateControls } from './ValidateControls';
import './assets/skin/theme.less';
import { ImageAnnotation } from './components/Annotation';
import App from './components/App';
import { ExportAuditEvents } from './components/AuditEvents';
import * as batches from './components/Batches';
import * as companies from './components/Companies';
import Dashboard from './components/Dashboard/Dashboard';
import { ImportMultipleSurveys } from './components/DataExchange/ImportMultipleSurveys';
import { ImportObjects } from './components/DataExchange/ImportObjects';
import { ImportSurvey } from './components/DataExchange/ImportSurvey';
import { ZxyDataExchange } from './components/DataExchange/zxyDataExchange';
import * as equipment from './components/Equipment';
import { Faq } from './components/Help/FAQ';
import { SupportHook } from './components/Help/SupportHook';
import { InspectionStandardDataAnnotationPage } from './components/InspectionStandardDataAnnotationPage';
import * as maintenanceMeasures from './components/MaintenanceMeasures';
import { NotFound } from './components/NotFound';
import * as objects from './components/Objects';
import { PanoramaViewer } from './components/PanoramaViewer';
import { Player } from './components/Player';
import { Profile } from './components/Profile/Profile';
import * as projects from './components/Projects';
import * as settings from './components/Settings';
import { Jobs, JobsFinished } from './components/Settings';
import * as surveys from './components/Surveys';
import { HubSpotDealList } from './components/hubSpotDeals';
import { LogOut } from './components/utils/LogOut';
import { Throw } from './components/utils/Throw';
import { configureStore } from './configureStore';
import { RouterPropsWrapper } from './mixins/router/withReactRouter';
import { fetchGlobalSettings } from './utils/getGlobalSettings';
import { MantineProvider } from '@mantine/core';
import { mantineCssVariablesResolver } from './mantineCssVariablesResolver';

async function load() {
    const globalSettings = await fetchGlobalSettings();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    (window as any).zxySettings = globalSettings; // TODO: ZXY-1110

    const { host } = window.location;

    initSentry({
        enabled: PRODUCTION && !host.includes('localhost'),
        dsn: 'https://030e479390024c3fbb7baf8a348c0b0a@sentry.io/1267668',
        environment: host,
        release: `zxy-cloud@${globalSettings.softwareVersion}`,
        // TODO: remove after testing with Sentry ZXY-1101
        beforeSend: (event, hint) => {
            console.log('hint', hint);
            console.log('event', event);
            if (
                hint.originalException &&
                hint.originalException instanceof Error &&
                'statusCode' in hint.originalException &&
                hint.originalException.statusCode === 401
            ) {
                return null;
            } else if (
                hint.originalException
                    ?.toString()
                    .includes('The play() request was interrupted because video-only background media was paused to save power.')
            ) {
                return null;
            }

            return event;
        },
    });

    const store = configureStore({});
    function AppRoute() {
        const location = useLocation();
        return (
            <MantineProvider cssVariablesResolver={mantineCssVariablesResolver()}>
                <App location={{ ...location, ...{ query: parseQueryString(location.search) } }} globalSettings={globalSettings}>
                    <Routes>
                        <Route path="/" element={<RouterPropsWrapper render={(p) => <Dashboard {...p} />} />} />
                        <Route path="/profile" element={<RouterPropsWrapper render={(p) => <Profile {...p} />} />} />
                        <Route path="/dashboard" element={<RouterPropsWrapper render={(p) => <Dashboard {...p} />} />} />
                        <Route path="/faq" element={<RouterPropsWrapper render={() => <Faq />} />} />
                        <Route path="/support" element={<RouterPropsWrapper render={(p) => <SupportHook {...p} />} />} />
                        <Route path="/annotater/:id" element={<RouterPropsWrapper render={(p) => <surveys.SurveyEditImageAnnotation {...p} />} />} />
                        <Route
                            path="/companies/addRelation"
                            element={<RouterPropsWrapper render={(p) => <companies.CompanyRelationAdd {...p} />} />}
                        />
                        <Route
                            path="/companies/addLicence"
                            element={<RouterPropsWrapper render={(p) => <companies.CompanyLicenceAddEdit {...p} />} />}
                        />
                        <Route path="/companies/addUser" element={<RouterPropsWrapper render={(p) => <companies.UserAddEdit {...p} />} />} />
                        <Route path="/companies/add" element={<RouterPropsWrapper render={(p) => <companies.CompanyAddEdit {...p} />} />} />
                        <Route path="/companies/:id/editUser" element={<RouterPropsWrapper render={(p) => <companies.UserAddEdit {...p} />} />} />
                        <Route path="/companies/:id/edit" element={<RouterPropsWrapper render={(p) => <companies.CompanyAddEdit {...p} />} />} />
                        <Route path="/companies/:id" element={<RouterPropsWrapper render={(p) => <companies.CompanyDetails {...p} />} />} />
                        <Route path="/companies" element={<RouterPropsWrapper render={(p) => <companies.CompaniesList {...p} />} />} />
                        <Route path="/objects" element={<RouterPropsWrapper render={(p) => <objects.ObjectsList {...p} />} />} />
                        <Route path="/objects/map" element={<RouterPropsWrapper render={(p) => <objects.ObjectsMap {...p} />} />} />
                        <Route path="/objects/add" element={<RouterPropsWrapper render={(p) => <objects.ObjectAddEdit showHeader {...p} />} />} />
                        <Route path="/objects/:id/importSurvey" element={<RouterPropsWrapper render={(p) => <ImportSurvey {...p} />} />} />
                        <Route
                            path="/objects/:id/surveyEditComposition"
                            element={<RouterPropsWrapper render={(p) => <objects.ObjectCompositionEdit showHeader {...p} />} />}
                        />
                        <Route path="/objects/:id/:tab" element={<RouterPropsWrapper render={(p) => <objects.ObjectTemplate {...p} />} />} />
                        <Route path="/objects/:id" element={<RouterPropsWrapper render={(p) => <objects.ObjectDetails {...p} />} />} />
                        <Route path="/surveys" element={<RouterPropsWrapper render={(p) => <surveys.SurveysList {...p} />} />} />
                        <Route
                            path="/surveys/furtherInvestigation"
                            element={<RouterPropsWrapper render={(p) => <surveys.SurveyInvestigationList {...p} />} />}
                        />
                        <Route path="/surveys/longRunning" element={<RouterPropsWrapper render={() => <surveys.LongRunningSurveys />} />} />
                        <Route path="/surveys/notCompleted" element={<RouterPropsWrapper render={() => <surveys.NotCompletedSurveys />} />} />
                        <Route path="/surveys/map" element={<RouterPropsWrapper render={(p) => <surveys.SurveysMap {...p} />} />} />
                        <Route path="/surveys/add" element={<RouterPropsWrapper render={(p) => <surveys.SurveyAddEdit {...p} />} />} />
                        <Route path="/surveys/filter/:status" element={<RouterPropsWrapper render={(p) => <surveys.SurveysList {...p} />} />} />
                        <Route path="/surveys/compare/:id" element={<RouterPropsWrapper render={(p) => <surveys.SurveyCompare {...p} />} />} />
                        <Route path="/surveys/:id/edit" element={<RouterPropsWrapper render={(p) => <surveys.SurveyAddEdit {...p} />} />} />
                        <Route path="/surveys/:id/:tab" element={<RouterPropsWrapper render={(p) => <surveys.SurveyTemplate {...p} />} />} />
                        <Route path="/surveys/:id" element={<RouterPropsWrapper render={(p) => <surveys.SurveyDetails {...p} />} />} />
                        <Route path="/batches" element={<RouterPropsWrapper render={(p) => <batches.BatchesList {...p} />} />} />
                        <Route path="/batches/map" element={<RouterPropsWrapper render={(p) => <batches.BatchesMap {...p} />} />} />
                        <Route path="/batches/add" element={<RouterPropsWrapper render={(p) => <batches.BatchAddEdit {...p} />} />} />
                        <Route path="/batches/:id/edit" element={<RouterPropsWrapper render={(p) => <batches.BatchAddEdit {...p} />} />} />
                        <Route path="/batches/:id/documents" element={<RouterPropsWrapper render={(p) => <batches.BatchDocuments {...p} />} />} />
                        <Route path="/batches/:id" element={<RouterPropsWrapper render={(p) => <batches.BatchDetails {...p} />} />} />
                        <Route path="/equipment" element={<equipment.EquipmentList />} />
                        <Route path="/equipment/add" element={<RouterPropsWrapper render={(p) => <equipment.EquipmentAddEdit {...p} />} />} />
                        <Route path="/equipment/:id/edit" element={<RouterPropsWrapper render={(p) => <equipment.EquipmentAddEdit {...p} />} />} />
                        <Route
                            path="/equipment/:id/documents"
                            element={<RouterPropsWrapper render={(p) => <equipment.EquipmentDocuments {...p} />} />}
                        />
                        <Route path="/equipment/:id/payload" element={<RouterPropsWrapper render={(p) => <equipment.EquipmentPayload {...p} />} />} />
                        <Route path="/equipment/:id" element={<RouterPropsWrapper render={(p) => <equipment.EquipmentDetails {...p} />} />} />
                        <Route
                            path="/maintenanceMeasures/list"
                            element={<RouterPropsWrapper render={(p) => <maintenanceMeasures.MaintenanceMeasuresList {...p} />} />}
                        />
                        <Route
                            path="/maintenanceMeasures/map"
                            element={<RouterPropsWrapper render={(p) => <maintenanceMeasures.MaintenanceMeasuresMap {...p} />} />}
                        />
                        <Route path="/projects" element={<RouterPropsWrapper render={() => <projects.ImportProjectList />} />} />

                        <Route path="/projects/:id/import" element={<RouterPropsWrapper render={(p) => <projects.ImportProject {...p} />} />} />
                        <Route path="/settings/taskDefinitions" element={<RouterPropsWrapper render={() => <settings.TaskDefinitionsList />} />} />
                        <Route
                            path="/settings/taskDefinitions/add"
                            element={<RouterPropsWrapper render={(p) => <settings.TaskDefinitionAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/taskDefinitions/:id/edit"
                            element={<RouterPropsWrapper render={(p) => <settings.TaskDefinitionAddEdit {...p} />} />}
                        />

                        <Route path="/settings/inspection-forms" element={<settings.InspectionFormsList />} />
                        <Route
                            path="/settings/inspection-forms/add"
                            element={<RouterPropsWrapper render={(p) => <settings.InspectionFormAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/inspection-forms/:id/edit"
                            element={<RouterPropsWrapper render={(p) => <settings.InspectionFormAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/inspection-forms/:id/companies"
                            element={<RouterPropsWrapper render={(p) => <settings.InspectionFormCompanies {...p} />} />}
                        />

                        <Route path="/settings/objecttypes" element={<settings.ObjectTypesList />} />
                        <Route
                            path="/settings/objecttypes/add"
                            element={<RouterPropsWrapper render={(p) => <settings.ObjectTypeAddEdit {...p} />} />}
                        />
                        <Route path="/settings/objectTypes" element={<settings.ObjectTypesList />} />
                        <Route
                            path="/settings/objectTypes/add"
                            element={<RouterPropsWrapper render={(p) => <settings.ObjectTypeAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/objectTypes/:objectTypeId/objectTypeFields/:objectTypeFieldId/edit"
                            element={<RouterPropsWrapper render={(p) => <settings.ObjectTypeFieldAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/objectTypes/:objectTypeId/objectTypeFields/add"
                            element={<RouterPropsWrapper render={(p) => <settings.ObjectTypeFieldAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/objectTypes/:objectTypeId/objectTypeFields"
                            element={<RouterPropsWrapper render={(p) => <settings.ObjectTypeFieldList {...p} />} />}
                        />
                        <Route
                            path="/settings/objectTypes/:id/edit"
                            element={<RouterPropsWrapper render={(p) => <settings.ObjectTypeAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/objectTypes/:id/inspectionStandards"
                            element={<RouterPropsWrapper render={(p) => <settings.InspectionStandardsList {...p} />} />}
                        />
                        <Route
                            path="/settings/objectTypes/inspectionStandards/:id/editObjectTemplate"
                            element={<RouterPropsWrapper render={(p) => <settings.ObjectTemplateEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/objectTypes/inspectionStandards/:id/import"
                            element={<RouterPropsWrapper render={(p) => <settings.ImportCompositionStandard {...p} />} />}
                        />
                        <Route path="/settings/equipmentTypes" element={<settings.EquipmentTypesList />} />
                        <Route
                            path="/settings/equipmentTypes/add"
                            element={<RouterPropsWrapper render={(p) => <settings.EquipmentTypesAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/equipmentTypes/:id/edit"
                            element={<RouterPropsWrapper render={(p) => <settings.EquipmentTypesAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/elementCodes"
                            element={<RouterPropsWrapper render={(p) => <settings.ObjectTypeElementCodes {...p} />} />}
                        />
                        <Route path="/settings/registrations" element={<settings.RegistrationsList />} />
                        <Route
                            path="/settings/registrations/add"
                            element={<RouterPropsWrapper render={(p) => <settings.RegistrationAddEdit {...p} />} />}
                        />
                        <Route
                            path="/settings/registrations/:id/edit/key/:key"
                            element={<RouterPropsWrapper render={(p) => <settings.RegistrationAddEdit {...p} />} />}
                        />
                        <Route path="/settings/unitCodes" element={<RouterPropsWrapper render={(p) => <settings.ObjectTypeUnitCodes {...p} />} />} />
                        <Route
                            path="/settings/unitCodes/:id/defaultMaintenance"
                            element={<RouterPropsWrapper render={(p) => <settings.DefaultMaintenanceMeasures {...p} />} />}
                        />
                        <Route
                            path="/settings/unitCodes/:id/defaultFailureMode"
                            element={<RouterPropsWrapper render={(p) => <settings.DefaultFailureModes {...p} />} />}
                        />
                        <Route path="/settings/surveyLabels" element={<settings.SurveyLabelsList />} />
                        <Route path="/settings/objectLabels" element={<settings.ObjectLabelsList />} />
                        <Route path="/auditLog" element={<ExportAuditEvents />} />
                        <Route path="/jobs" element={<Jobs />} />
                        <Route path="/finished" element={<JobsFinished />} />
                        <Route path="/import/objects" element={<ImportObjects />} />
                        <Route path="/import/surveys" element={<ImportMultipleSurveys />} />
                        <Route path="/zxyExchange" element={<ZxyDataExchange />} />
                        <Route path="/hubSpotDeals" element={<HubSpotDealList />} />
                        <Route path="/app" element={<RouterPropsWrapper render={(p) => <Dashboard {...p} />} />} />
                        <Route path="/throw" element={<Throw />} />
                        <Route path="/logOut" element={<LogOut />} />
                        <Route element={<NotFound />} />
                    </Routes>
                </App>
            </MantineProvider>
        );
    }

    if (document.getElementById('app')) {
        const container = document.getElementById('app');
        const root = createRoot(container!);
        root.render(
            <Provider store={store}>
                <BrowserRouter>
                    <AppRoute />
                </BrowserRouter>
            </Provider>,
        );
    }

    if (document.getElementById('playerComponent')) {
        const container = document.getElementById('playerComponent');
        const root = createRoot(container!);
        root.render(<Player />);
    }

    if (document.getElementById('annotationComponent')) {
        const container = document.getElementById('annotationComponent');
        const root = createRoot(container!);
        root.render(<ImageAnnotation />);
    }
    if (document.getElementById('inspectionStandardDataAnnotationPage')) {
        const container = document.getElementById('inspectionStandardDataAnnotationPage');
        const root = createRoot(container!);
        root.render(<InspectionStandardDataAnnotationPage />);
    }
    if (document.getElementById('panoramaComponent')) {
        const container = document.getElementById('panoramaComponent');
        const root = createRoot(container!);
        root.render(<PanoramaViewer />);
    }
    if (document.getElementById('validateControlsComponent')) {
        const container = document.getElementById('validateControlsComponent');
        const root = createRoot(container!);
        root.render(<ValidateControls globalSettings={globalSettings} />);
    }
}

void load();
